import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Archive, Book, CheckCircle2, CreditCard, Download, FileText, Gift, Loader2, Minus, Pencil, Plus, XCircle } from 'lucide-react';
import confetti, { reset } from 'canvas-confetti';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { paymentMethodConfig, defaultPaymentMethod } from '~/lib/payment-method-config';
import AddPaymentDialog from './AddPaymentDialog';
import BillingDraftEditor from './BillingDraftEditor';
import RefundPaymentDialog from './RefundPaymentDialog';
import BillingDraftLogsModal from './BillingDraftLogsModal';
import { cancellationFeeTypeLabels } from '~/lib/cancellation-fee-config';

const statusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PENDING_PAYMENT: { label: 'Ausstehend', variant: 'outline' },
    PAID: { label: 'Bezahlt', variant: 'default' },
    CANCELLED: { label: 'Storniert', variant: 'destructive' },
};

const invoiceTypeLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    GOA_INVOICE: { label: 'GOÄ', variant: 'default' },
    GOA_REFUND_INVOICE: { label: 'GOÄ Gutschrift', variant: 'destructive' },
    BUNDLE_INVOICE: { label: 'Bundle', variant: 'secondary' },
    BUNDLE_REFUND_INVOICE: { label: 'Bundle Gutschrift', variant: 'destructive' },
    GIFT_CARD_INVOICE: { label: 'Gutscheine', variant: 'secondary' },
    GIFT_CARD_REFUND_INVOICE: { label: 'Gutschein Gutschrift', variant: 'destructive' },
    GENERAL_INVOICE: { label: 'Allgemein', variant: 'outline' },
    GENERAL_REFUND_INVOICE: { label: 'Allgemein Gutschrift', variant: 'destructive' },
    AUSFALL_INVOICE: { label: 'Ausfallentschädigung', variant: 'secondary' },
    AUSFALL_REFUND_INVOICE: { label: 'Ausfallentschädigung Gutschrift', variant: 'destructive' },
};

const paymentStatusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PENDING: { label: 'Ausstehend', variant: 'outline' },
    COMPLETED: { label: 'Abgeschlossen', variant: 'default' },
    FAILED: { label: 'Fehlgeschlagen', variant: 'destructive' },
    REFUNDED: { label: 'Erstattet', variant: 'secondary' },
};

const lineItemStatusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PENDING_PAYMENT: { label: 'Ausstehend', variant: 'outline' },
    PAID: { label: 'Bezahlt', variant: 'default' },
    CANCELLED: { label: 'Storniert', variant: 'destructive' },
};

type BillingDraftDetailDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    billingDraftId: string;
};

const BillingDraftDetailDialog = ({
    open,
    onOpenChange,
    billingDraftId,
}: BillingDraftDetailDialogProps) => {
    const id = billingDraftId;
    const trpcUtils = trpc.useUtils();
    const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
    const [showRefundPaymentDialog, setShowRefundPaymentDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [cancelLineItemTarget, setCancelLineItemTarget] = useState<any | null>(null);
    const [reverseBundleVoucher, setReverseBundleVoucher] = useState(true);
    const [reverseGiftCard, setReverseGiftCard] = useState(true);
    const [isLastActiveLineItem, setIsLastActiveLineItem] = useState(false);
    // Bundle cancellation state
    const [bundleCancelCheckTarget, setBundleCancelCheckTarget] = useState<{ lineItemId: string } | null>(null);
    const [bundleCancelWarningInfo, setBundleCancelWarningInfo] = useState<{
        vouchers: Array<{ treatmentVariantName: string; unitQuantity: number; usedUnits: number; remainingUnits: number }>;
    } | null>(null);
    const [bundleCancelConfirmInfo, setBundleCancelConfirmInfo] = useState<{
        lineItemId: string;
        vouchers: Array<{ treatmentVariantName: string; unitQuantity: number; remainingUnits: number }>;
    } | null>(null);

    // Gift card cancellation state
    const [giftCardCancelCheckTarget, setGiftCardCancelCheckTarget] = useState<{ lineItemId: string } | null>(null);
    const [giftCardCancelWarningInfo, setGiftCardCancelWarningInfo] = useState<{
        giftCards: Array<{ giftCardNumber: string; initialValueEur: number; usedAmount: number; remainingBalance: number }>;
    } | null>(null);
    const [giftCardCancelConfirmInfo, setGiftCardCancelConfirmInfo] = useState<{
        lineItemId: string;
        giftCards: Array<{ giftCardNumber: string; initialValueEur: number; remainingBalance: number }>;
    } | null>(null);

    const { data: billingDraft, isLoading, error } = trpc.billingDrafts.getOne.useQuery(
        { id },
        { enabled: !!id && open, refetchInterval: open ? 5_000 : false }
    );

    const { data: payments } = trpc.payments.getByBillingDraft.useQuery(
        { billingDraftId: id },
        { enabled: !!id && open, refetchInterval: open ? 5_000 : false }
    );

    const { data: customerGiftCards } = trpc.giftCards.getByCustomer.useQuery(
        { customerId: billingDraft?.customerId! },
        { enabled: !!billingDraft?.customerId }
    );
    const hasAvailableGiftCard = (customerGiftCards?.data ?? []).some((gc) => gc.remainingBalance > 0);

    // Check if there are Klarna authorizations available
    const { data: klarnaAuthorizations } = trpc.payments.getKlarnaAuthorizationsForBillingDraft.useQuery(
        { billingDraftId: id },
        { enabled: !!id && open, refetchInterval: open ? 5_000 : false }
    );
    const hasKlarnaAuthorization = (klarnaAuthorizations ?? []).length > 0;
    const { data: expiredKlarnaAuthorizations } = trpc.payments.getExpiredKlarnaAuthorizationsForBillingDraft.useQuery(
        { billingDraftId: id },
        { enabled: !!id && open }
    );
    const latestExpiredKlarnaAuth = expiredKlarnaAuthorizations?.[0];

    const { data: invoices, isLoading: invoicesLoading } = trpc.invoices.getByBillingDraft.useQuery(
        { billingDraftId: id },
        { enabled: !!id && open, refetchInterval: open ? 5_000 : false }
    );

    const prevStatusRef = useRef(billingDraft?.status);
    React.useEffect(() => {
        const prevStatus = prevStatusRef.current;
        const currentStatus = billingDraft?.status;
        prevStatusRef.current = currentStatus;

        if (prevStatus && prevStatus !== 'PAID' && currentStatus === 'PAID') {
            const kaliaShape = confetti.shapeFromPath({
                path: 'M31 42.993C29.0651 40.1609 18.6437 24.9259 15.4349 20.2311C16.3676 19.8752 19.0868 18.8777 22.0633 21.218C22.744 21.7538 23.5373 22.182 24.4502 22.3706C27.3714 22.9768 30.2353 21.1317 30.8499 18.2521C31.4645 15.3707 29.5957 12.5458 26.6746 11.9395C25.449 11.6858 23.8374 11.8479 22.7869 12.3325C20.55 13.3652 16.2354 18.5675 15.1116 20.284C15.108 20.2911 15.1062 20.2946 15.1026 20.2999L9.96252 26.6917C9.96252 16.0246 9.9643 3.55279 9.9643 0L0.00566757 12.3484C0.0324669 20.8462 -0.0157719 42.5348 0.00566757 43H9.95894C9.95894 41.3364 9.95894 34.5604 9.95894 26.8063C10.5843 27.6716 18.5669 38.7229 21.4862 43L30.9964 42.993H31Z',
            });
            const burst = (angle: number, origin: { x: number; y: number }) => {
                confetti({
                    particleCount: 40,
                    angle,
                    spread: 60,
                    origin,
                    shapes: [kaliaShape],
                    scalar: 1.5,
                    flat: true,
                    colors: ['#FF6439'],
                    gravity: 0.9,
                    ticks: 200,
                });
                confetti({
                    particleCount: 200,
                    angle,
                    spread: 60,
                    origin,
                    colors: ['#561C2C'],
                    gravity: 0.9,
                    ticks: 200,
                });
            };
            burst(60, { x: 0, y: 0.6 });
            burst(120, { x: 1, y: 0.6 });
        }
    }, [billingDraft?.status]);

    const cancelMutation = trpc.billingDrafts.cancel.useMutation({
        onSuccess: (data) => {
            const count = data.cancelledInvoices.length;
            if (count > 0) {
                toast.success(`Auftrag storniert. ${count} Storno-Rechnung(en) erstellt.`);
            } else {
                toast.success('Auftrag wurde storniert');
            }
            trpcUtils.billingDrafts.getOne.invalidate({ id });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id });
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const downloadInvoiceMutation = trpc.invoices.getDownloadUrl.useMutation({
        onSuccess: (data) => {
            window.open(data.downloadUrl, '_blank');
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const cancelLineItemMutation = trpc.billingDrafts.cancelLineItem.useMutation({
        onSuccess: (data) => {
            const count = data.cancelledInvoices.length;
            if (count === 0) {
                toast.success('Position storniert.');
            } else if (count === 1) {
                toast.success(`Position storniert. Storno-Rechnung ${data.cancelledInvoices[0].invoiceNumber} erstellt.`);
            } else {
                toast.success(`Position storniert. ${count} Storno-Rechnungen erstellt.`);
            }
            trpcUtils.billingDrafts.getOne.invalidate({ id });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id });
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const cancelBundleWithArchiveMutation = trpc.billingDrafts.cancelBundleWithArchive.useMutation({
        onSuccess: (data) => {
            const count = data.cancelledInvoices.length;
            if (count === 1) {
                toast.success(`Bundle storniert und archiviert. Storno-Rechnung ${data.cancelledInvoices[0].invoiceNumber} erstellt.`);
            } else {
                toast.success(`Bundle storniert und archiviert. ${count} Storno-Rechnungen erstellt.`);
            }
            trpcUtils.billingDrafts.getOne.invalidate({ id });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id });
            setBundleCancelConfirmInfo(null);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const cancelGiftCardWithArchiveMutation = trpc.billingDrafts.cancelGiftCardWithArchive.useMutation({
        onSuccess: (data) => {
            const count = data.cancelledInvoices.length;
            if (count === 1) {
                toast.success(`Gutscheinkarte storniert und archiviert. Storno-Rechnung ${data.cancelledInvoices[0].invoiceNumber} erstellt.`);
            } else {
                toast.success(`Gutscheinkarte storniert und archiviert. ${count} Storno-Rechnungen erstellt.`);
            }
            trpcUtils.billingDrafts.getOne.invalidate({ id });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id });
            setGiftCardCancelConfirmInfo(null);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const archiveMutation = trpc.billingDrafts.archive.useMutation({
        onSuccess: () => {
            toast.success('Auftrag wurde archiviert');
            onOpenChange(false);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const generateInvoicesMutation = trpc.billingDrafts.generateInvoices.useMutation({
        onSuccess: (data) => {
            toast.success(`${data.invoicesCount} Rechnung(en) erfolgreich erstellt`);
            trpcUtils.billingDrafts.getOne.invalidate({ id });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id });
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    // Fetch bundle cancellation info when a check target is set
    const { data: bundleCancellationInfo, isFetching: isFetchingBundleCancelInfo } = trpc.billingDrafts.getBundleCancellationInfo.useQuery(
        bundleCancelCheckTarget!,
        {
            enabled: !!bundleCancelCheckTarget,
            gcTime: 0,
            staleTime: 0,
        }
    );

    React.useEffect(() => {
        if (!bundleCancelCheckTarget || !bundleCancellationInfo || isFetchingBundleCancelInfo) {
            return;
        }

        if (!bundleCancellationInfo.canCancel) {
            setBundleCancelWarningInfo({ vouchers: bundleCancellationInfo.vouchers });
        } else {
            setBundleCancelConfirmInfo({
                lineItemId: bundleCancelCheckTarget.lineItemId,
                vouchers: bundleCancellationInfo.vouchers,
            });
        }

        setBundleCancelCheckTarget(null);
    }, [bundleCancellationInfo, bundleCancelCheckTarget, isFetchingBundleCancelInfo]);

    const handleBundleCancelCheck = (lineItemId: string) => {
        setBundleCancelCheckTarget({ lineItemId });
    };

    // Fetch gift card cancellation info when a check target is set
    const { data: giftCardCancellationInfo, isFetching: isFetchingGiftCardCancelInfo } = trpc.billingDrafts.getGiftCardCancellationInfo.useQuery(
        giftCardCancelCheckTarget!,
        {
            enabled: !!giftCardCancelCheckTarget,
            gcTime: 0,
            staleTime: 0,
        }
    );

    React.useEffect(() => {
        if (!giftCardCancelCheckTarget || !giftCardCancellationInfo || isFetchingGiftCardCancelInfo) {
            return;
        }

        if (!giftCardCancellationInfo.canCancel) {
            setGiftCardCancelWarningInfo({ giftCards: giftCardCancellationInfo.giftCards });
        } else {
            setGiftCardCancelConfirmInfo({
                lineItemId: giftCardCancelCheckTarget.lineItemId,
                giftCards: giftCardCancellationInfo.giftCards,
            });
        }

        setGiftCardCancelCheckTarget(null);
    }, [giftCardCancellationInfo, giftCardCancelCheckTarget, isFetchingGiftCardCancelInfo]);

    const handleGiftCardCancelCheck = (lineItemId: string) => {
        setGiftCardCancelCheckTarget({ lineItemId });
    };

    const formatCurrency = (amount: number | string) => {
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(num);
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className='space-y-4'>
                    <Skeleton className='h-8 w-48' />
                    <Skeleton className='h-48' />
                    <Skeleton className='h-64' />
                </div>
            );
        }

        if (error || !billingDraft) {
            return (
                <div className='text-center py-10'>
                    <h2 className='text-lg font-bold mb-2'>Auftrag nicht gefunden</h2>
                    <Button variant='outline' onClick={() => onOpenChange(false)}>
                        Schließen
                    </Button>
                </div>
            );
        }

        const status = statusLabels[billingDraft.status];

        // Invoices are never created for MARKETING, nor for INFLUENCER drafts whose
        // subtype is not invoice-eligible (only INFLUENCER / INFLUENCER_FLATRATE are, matching the cron).
        const influencerSubtypeInvoiceEligible = (billingDraft as any).influencerType === 'INFLUENCER' || (billingDraft as any).influencerType === 'INFLUENCER_FLATRATE';
        const invoicesNeverCreated = billingDraft.type === 'MARKETING'
            || billingDraft.type === 'KULANZ'
            || (billingDraft.type === 'INFLUENCER' && !influencerSubtypeInvoiceEligible);

        return (
            <div className='space-y-6'>
                {/* Header with name and status */}
                <div className='flex items-center justify-between'>
                    <div>
                        <h2 className='text-xl font-bold'>{billingDraft.billingDraftNumber}</h2>
                        <p className='text-sm text-muted-foreground'>
                            {billingDraft.customerName}
                        </p>
                    </div>
                    <Badge variant={status.variant} className='text-sm'>
                        {status.label}
                    </Badge>
                </div>


                {billingDraft.status === 'PAID' && (
                    <div className='flex items-center gap-3 rounded-lg border border-green-300 bg-green-50 p-4 dark:border-green-700 dark:bg-green-950/40'>
                        <CheckCircle2 className='h-6 w-6 shrink-0 text-green-600 dark:text-green-400' />
                        <div>
                            <p className='text-base font-semibold text-green-800 dark:text-green-300'>
                                Vollständig bezahlt
                            </p>
                            <p className='text-sm text-green-700 dark:text-green-400'>
                                Dieser Auftrag wurde vollständig bezahlt.
                            </p>
                        </div>
                    </div>
                )}

                <div className='flex flex-col gap-6'>
                    {/* Actions & Payments - side by side at top */}
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        {/* Actions */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Aktionen</CardTitle>
                            </CardHeader>
                            <CardContent className='space-y-2'>
                                {billingDraft.status !== 'CANCELLED' && (
                                    <>
                                        {billingDraft.type !== 'INFLUENCER' && billingDraft.type !== 'MARKETING' && billingDraft.type !== 'KULANZ' && (() => {
                                            const totalPaidCents = payments?.reduce((sum, p) =>
                                                p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                                            ) ?? 0;
                                            const totalPaidEur = totalPaidCents / 100;
                                            const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                                            const remaining = billingDraftTotal - totalPaidEur;
                                            return remaining > 0.01;
                                        })() && (
                                            <Button
                                                className='w-full'
                                                onClick={() => setShowAddPaymentDialog(true)}
                                            >
                                                <Plus className='mr-2 h-4 w-4' />
                                                Zahlung hinzufügen
                                            </Button>
                                        )}
                                        <Button
                                            variant='outline'
                                            className='w-full'
                                            disabled={!invoices || invoicesLoading || invoices.length > 0 || billingDraft.type === 'CANCELLATION_FEE'}
                                            title={billingDraft.type === 'CANCELLATION_FEE'
                                                ? 'Ausfallentschädigungen können nicht bearbeitet werden'
                                                : invoices && invoices.length > 0 ? 'Bearbeitung nicht möglich, da bereits Rechnungen erstellt wurden' : undefined}
                                            onClick={() => setShowEditDialog(true)}
                                        >
                                            <Pencil className='mr-2 h-4 w-4' />
                                            Bearbeiten
                                        </Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button variant='outline' className='w-full'>
                                                    <XCircle className='mr-2 h-4 w-4' />
                                                    Stornieren
                                                </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Auftrag stornieren?</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        Diese Aktion kann nicht rückgängig gemacht werden. Der Auftrag wird als storniert markiert.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        onClick={() => cancelMutation.mutate({ id: billingDraft.id })}
                                                    >
                                                        Stornieren
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </>
                                )}

                                {(!invoices || invoices.length === 0) && (
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant='ghost' className='w-full text-muted-foreground'>
                                                <Archive className='mr-2 h-4 w-4' />
                                                Archivieren
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Auftrag archivieren?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    Der Auftrag wird archiviert und aus der Übersicht entfernt.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => archiveMutation.mutate({ id: billingDraft.id })}
                                                >
                                                    Archivieren
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                )}
                                <Button
                                    variant='outline'
                                    className='w-full'
                                    onClick={() => setShowLogsModal(true)}
                                >
                                    <Book className='mr-2 h-4 w-4' />
                                    Logs anzeigen
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Payments */}
                        {billingDraft.type !== 'INFLUENCER' && billingDraft.type !== 'MARKETING' && billingDraft.type !== 'KULANZ' && <Card>
                            <CardHeader>
                                <CardTitle>Zahlungen</CardTitle>
                                <CardDescription>
                                    {(() => {
                                        const totalPaidCents = payments?.reduce((sum, p) =>
                                            p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                                        ) ?? 0;
                                        const totalPaidEur = totalPaidCents / 100;
                                        const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                                        const remaining = billingDraftTotal - totalPaidEur;

                                        if (remaining > 0.01) {
                                            return <span className='text-red-600 font-medium'>Offen: {formatCurrency(remaining)}</span>;
                                        }
                                        if (remaining < -0.01) {
                                            return <span className='text-red-600 font-medium'>Überzahlung: {formatCurrency(Math.abs(remaining))}</span>;
                                        }
                                        return <span className='text-green-600 font-medium'>Vollständig bezahlt</span>;
                                    })()}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className='space-y-3'>
                                {payments && payments.length > 0 ? (
                                    <div className='space-y-2'>
                                        {payments.map((payment) => {
                                            const method = paymentMethodConfig[payment.paymentMethod ?? ''] ?? defaultPaymentMethod;
                                            const pStatus = paymentStatusLabels[payment.status] ?? paymentStatusLabels.PENDING;
                                            const Icon = method.icon;
                                            const isRefund = payment.type === 'BILLING_DRAFT_REFUND';

                                            return (
                                                <div key={payment.id} className='flex items-center justify-between py-2 border-b last:border-0'>
                                                    <div className='flex items-center gap-2'>
                                                        <Icon className={`h-4 w-4 ${isRefund ? 'text-destructive' : 'text-muted-foreground'}`} />
                                                        <div>
                                                            <p className={`text-sm font-medium ${isRefund ? 'text-destructive' : ''}`}>
                                                                {formatCurrency(payment.paidAmountCents / 100)}
                                                                {isRefund && <span className='ml-1 text-xs font-normal'>(Rückzahlung)</span>}
                                                            </p>
                                                            <p className='text-xs text-muted-foreground'>
                                                                {method.label} &bull; {format(new Date(payment.createdAt), 'dd.MM.yy HH:mm', { locale: de })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Badge variant={pStatus.variant} className='text-xs'>
                                                        {pStatus.label}
                                                    </Badge>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className='text-sm text-muted-foreground text-center py-2'>
                                        Keine Zahlungen vorhanden
                                    </p>
                                )}

                                {(() => {
                                    const totalPaidCents = payments?.reduce((sum, p) =>
                                        p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                                    ) ?? 0;
                                    const totalPaidEur = totalPaidCents / 100;
                                    const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                                    const remaining = billingDraftTotal - totalPaidEur;
                                    return remaining > 0.01 && billingDraft.status !== 'CANCELLED';
                                })() && (
                                    <>
                                        {hasAvailableGiftCard && (
                                            <div className='flex items-center gap-1.5 justify-center text-orange-500 text-sm font-medium'>
                                                <Gift className='h-4 w-4' />
                                                <span>Gutschein verfügbar</span>
                                            </div>
                                        )}
                                        {hasKlarnaAuthorization && (
                                            <div className='flex items-center gap-1.5 justify-center text-blue-600 text-sm font-medium'>
                                                <CreditCard className='h-4 w-4' />
                                                <span>Klarna-Vorautorisierung verfügbar</span>
                                            </div>
                                        )}
                                        {!hasKlarnaAuthorization && latestExpiredKlarnaAuth?.expiresAt && (
                                            <div className='flex items-center gap-1.5 justify-center text-amber-600 text-sm font-medium'>
                                                <CreditCard className='h-4 w-4' />
                                                <span>
                                                    Klarna-Vorautorisierung abgelaufen am{' '}
                                                    {format(new Date(latestExpiredKlarnaAuth.expiresAt), 'dd.MM.yyyy', { locale: de })}
                                                </span>
                                            </div>
                                        )}
                                        <Button
                                            variant='outline'
                                            className='w-full'
                                            onClick={() => setShowAddPaymentDialog(true)}
                                        >
                                            <Plus className='mr-2 h-4 w-4' />
                                            Zahlung hinzufügen
                                        </Button>
                                    </>
                                )}
                                {(() => {
                                    const totalPaidCents = payments?.reduce((sum, p) =>
                                        p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                                    ) ?? 0;
                                    const totalPaidEur = totalPaidCents / 100;
                                    const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                                    const overpayment = totalPaidEur - billingDraftTotal;
                                    return overpayment > 0.01;
                                })() && (
                                    <Button
                                        variant='outline'
                                        className='w-full text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive'
                                        onClick={() => setShowRefundPaymentDialog(true)}
                                    >
                                        <Minus className='mr-2 h-4 w-4' />
                                        Rückzahlung hinzufügen
                                    </Button>
                                )}
                            </CardContent>
                        </Card>}
                    </div>

                    {/* Main Content */}
                    <div className='space-y-6'>
                        {/* Line Items */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Positionen</CardTitle>
                            </CardHeader>
                            <CardContent className='p-0'>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className='w-12'>Pos.</TableHead>
                                            <TableHead>Beschreibung</TableHead>
                                            <TableHead className='text-right'>Netto</TableHead>
                                            <TableHead className='text-right'>MwSt.</TableHead>
                                            <TableHead className='text-right'>Brutto</TableHead>
                                            {billingDraft.status !== 'CANCELLED' && (
                                                <TableHead className='w-12'></TableHead>
                                            )}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {billingDraft.lineItems.map((item) => {
                                            const isCancelled = item.status === 'CANCELLED';
                                            const statusInfo = lineItemStatusLabels[item.status] || { label: item.status, variant: 'outline' as const };

                                            return (
                                                <TableRow key={item.id} className={isCancelled ? 'opacity-60' : ''}>
                                                    <TableCell className={`font-mono text-sm ${isCancelled ? 'line-through' : ''}`}>
                                                        {item.sequence}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className='space-y-1'>
                                                            <div className='flex items-center gap-2'>
                                                                <p className={`font-medium ${isCancelled ? 'line-through' : ''}`}>
                                                                    {item.description}
                                                                </p>
                                                                {item.cancellationFeeType && (
                                                                    <Badge variant='secondary'>
                                                                        {cancellationFeeTypeLabels[item.cancellationFeeType]}
                                                                    </Badge>
                                                                )}
                                                                {isCancelled && (
                                                                    <Badge variant={statusInfo.variant}>
                                                                        {statusInfo.label}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            {(item ).discounts && (item ).discounts.length > 0 && !isCancelled && (
                                                                <div className='flex flex-wrap gap-1'>
                                                                    {(item ).discounts.map((d: any) => (
                                                                        <Badge key={d.id} variant='outline' className='text-[10px] px-1.5 py-0 text-green-700 border-green-300'>
                                                                            {d.discountCode?.name || 'Manueller Rabatt'}: -{formatCurrency(Number(d.discountAmountEur))}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className={`text-right font-mono ${isCancelled ? 'line-through' : ''}`}>
                                                        {formatCurrency(Number((item ).totalNetDiscounted))}
                                                    </TableCell>
                                                    <TableCell className={`text-right font-mono ${isCancelled ? 'line-through' : ''}`}>
                                                        {formatCurrency(Number((item ).vatAmountDiscounted))}
                                                    </TableCell>
                                                    <TableCell className={`text-right font-mono font-medium ${isCancelled ? 'line-through' : ''}`}>
                                                        {Number((item ).grossDiscountAmount) > 0 && !isCancelled && (
                                                            <div className='text-xs text-muted-foreground line-through font-normal'>
                                                                {formatCurrency(Number(item.totalGross))}
                                                            </div>
                                                        )}
                                                        {formatCurrency(Number((item ).totalGrossDiscounted))}
                                                    </TableCell>
                                                    {billingDraft.status !== 'CANCELLED' && (
                                                        <TableCell className='text-center'>
                                                            {!isCancelled && (
                                                                <Button
                                                                    variant='ghost'
                                                                    size='icon'
                                                                    className='h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10'
                                                                    disabled={cancelLineItemMutation.isPending || cancelBundleWithArchiveMutation.isPending || cancelGiftCardWithArchiveMutation.isPending || isFetchingBundleCancelInfo || isFetchingGiftCardCancelInfo}
                                                                    onClick={() => {
                                                                        const activeLineItems = billingDraft.lineItems.filter((li: any) => li.status !== 'CANCELLED');
                                                                        const lastActive = activeLineItems.length === 1;
                                                                        setIsLastActiveLineItem(lastActive);

                                                                        const isBundlePurchaseItem = !!item.bundle;
                                                                        const isGiftCardPurchaseItem = !!(item ).giftCardTemplateId;
                                                                        if (isBundlePurchaseItem) {
                                                                            handleBundleCancelCheck(item.id);
                                                                        } else if (isGiftCardPurchaseItem) {
                                                                            handleGiftCardCancelCheck(item.id);
                                                                        } else {
                                                                            setCancelLineItemTarget(item);
                                                                            setReverseBundleVoucher(true);
                                                                            setReverseGiftCard(lastActive);
                                                                        }
                                                                    }}
                                                                >
                                                                    {(isFetchingBundleCancelInfo && bundleCancelCheckTarget?.lineItemId === item.id) || (isFetchingGiftCardCancelInfo && giftCardCancelCheckTarget?.lineItemId === item.id) ? (
                                                                        <Loader2 className='h-4 w-4 animate-spin' />
                                                                    ) : (
                                                                        <XCircle className='h-4 w-4' />
                                                                    )}
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                <Separator />
                                <div className='p-4 space-y-2'>
                                    {(() => {
                                        const activeLineItems = billingDraft.lineItems.filter((li: any) => li.status !== 'CANCELLED');
                                        const totalDiscountAmount = activeLineItems.reduce((sum: number, li: any) => {
                                            return sum + ((li.discounts || [])).reduce((dSum: number, d: any) => dSum + Number(d.discountAmountEur), 0);
                                        }, 0);
                                        const totalGrossOriginal = activeLineItems.reduce((sum: number, li: any) => sum + Number(li.totalGross), 0);
                                        return totalDiscountAmount > 0 ? (
                                            <>
                                                <div className='flex justify-between text-sm text-muted-foreground'>
                                                    <span>Bruttobetrag:</span>
                                                    <span className='font-mono'>{formatCurrency(totalGrossOriginal)}</span>
                                                </div>
                                                <div className='flex justify-between text-sm text-green-600 font-medium'>
                                                    <span>Rabatt gesamt:</span>
                                                    <span className='font-mono'>-{formatCurrency(totalDiscountAmount)}</span>
                                                </div>
                                            </>
                                        ) : null;
                                    })()}
                                    <div className='flex justify-between text-sm'>
                                        <span>Nettobetrag:</span>
                                        <span className='font-mono'>{formatCurrency(Number(billingDraft.totalNetDiscounted))}</span>
                                    </div>
                                    <div className='flex justify-between text-sm'>
                                        <span>MwSt.:</span>
                                        <span className='font-mono'>{formatCurrency(Number(billingDraft.vatAmountDiscounted))}</span>
                                    </div>
                                    <Separator />
                                    <div className='flex justify-between text-lg font-bold'>
                                        <span>Gesamtbetrag:</span>
                                        <span className='font-mono'>{formatCurrency(Number(billingDraft.totalGrossDiscounted))}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Invoices Section */}
                        <Card>
                            <CardHeader>
                                <div className='flex items-center gap-2'>
                                    <CardTitle>Rechnungen</CardTitle>
                                    {invoices && invoices.length > 0 && (
                                        <Badge variant='secondary' className='text-xs'>
                                            {invoices.length}
                                        </Badge>
                                    )}
                                </div>
                                <CardDescription>
                                    {invoicesNeverCreated
                                        ? 'Für Influencer-/Marketing-/Academy-/Kulanz-Aufträge werden keine Rechnungen erstellt'
                                        : invoices && invoices.length > 0
                                            ? 'Rechnungen wurden erstellt'
                                            : 'Werden automatisch nach der ersten Zahlung oder nach 60 Minuten erstellt'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {/* Manual invoice generation - shown for NORMAL and for INFLUENCER subtypes that the cron also generates invoices for (INFLUENCER / INFLUENCER_FLATRATE). Hidden for MARKETING/ACADEMY and non-eligible influencer subtypes (BEAUTY_INSIDER / none). */}
                                {!invoicesLoading
                                    && (!invoices || invoices.length === 0)
                                    && !invoicesNeverCreated
                                    && billingDraft.status !== 'CANCELLED' && (
                                    <div className='mb-4'>
                                        <Button
                                            variant='outline'
                                            className='w-full'
                                            onClick={() => generateInvoicesMutation.mutate({ id: billingDraft.id })}
                                            disabled={generateInvoicesMutation.isPending}
                                        >
                                            {generateInvoicesMutation.isPending ? (
                                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            ) : (
                                                <FileText className='mr-2 h-4 w-4' />
                                            )}
                                            Rechnungen erstellen
                                        </Button>
                                    </div>
                                )}
                                {invoicesLoading ? (
                                    <div className='space-y-2'>
                                        <Skeleton className='h-12' />
                                        <Skeleton className='h-12' />
                                    </div>
                                ) : invoices && invoices.length > 0 ? (
                                    <div className='space-y-3'>
                                        {invoices.map((invoice) => {
                                            const typeInfo = invoiceTypeLabels[invoice.type] || { label: invoice.type, variant: 'outline' as const };
                                            const isRefund = invoice.type.includes('REFUND');
                                            const hasRefund = invoice.refundInvoices && invoice.refundInvoices.length > 0;
                                            const isRefundOf = !!invoice.refundSourceInvoice;

                                            return (
                                                <div key={invoice.id} className='border rounded-lg p-4'>
                                                    <div className='flex items-center justify-between mb-2'>
                                                        <div className='flex items-center gap-2'>
                                                            <Badge variant={typeInfo.variant}>
                                                                {typeInfo.label}
                                                            </Badge>
                                                            <span className='font-mono text-sm font-medium'>
                                                                {invoice.invoiceNumber}
                                                            </span>
                                                        </div>
                                                        <span className='text-sm text-muted-foreground'>
                                                            {format(new Date(invoice.invoiceDate), 'dd.MM.yyyy', { locale: de })}
                                                        </span>
                                                    </div>
                                                    <div className='flex items-center justify-between'>
                                                        <div className='text-sm'>
                                                            <span className='font-mono font-medium'>
                                                                {formatCurrency(invoice.totalGross)}
                                                            </span>
                                                            {isRefundOf && invoice.refundSourceInvoice && (
                                                                <span className='text-muted-foreground ml-2'>
                                                                    (Gutschrift zu {invoice.refundSourceInvoice.invoiceNumber})
                                                                </span>
                                                            )}
                                                            {hasRefund && invoice.refundInvoices.map((refund) => (
                                                                <span key={refund.id} className='text-muted-foreground ml-2'>
                                                                    &rarr; Gutschrift: {refund.invoiceNumber}
                                                                </span>
                                                            ))}
                                                        </div>
                                                        <Button
                                                            variant='ghost'
                                                            size='sm'
                                                            onClick={() => downloadInvoiceMutation.mutate({ id: invoice.id })}
                                                            disabled={downloadInvoiceMutation.isPending}
                                                        >
                                                            {downloadInvoiceMutation.isPending ? (
                                                                <Loader2 className='h-4 w-4 animate-spin' />
                                                            ) : (
                                                                <Download className='h-4 w-4' />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : invoicesNeverCreated ? (
                                    <div className='flex flex-col items-center gap-2 py-6'>
                                        <FileText className='h-6 w-6 text-muted-foreground' />
                                        <p className='text-sm text-muted-foreground text-center'>
                                            Für Influencer-/Marketing-/Academy-Aufträge werden keine Rechnungen erstellt.
                                        </p>
                                    </div>
                                ) : (
                                    <div className='flex flex-col items-center gap-2 py-6'>
                                        <FileText className='h-6 w-6 text-muted-foreground' />
                                        <p className='text-sm text-muted-foreground text-center'>
                                            Noch keine Rechnungen erstellt. Rechnungen werden automatisch nach der ersten Zahlung oder nach 60 Minuten generiert.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                </div>

                <BillingDraftLogsModal
                    billingDraftId={billingDraft.id}
                    open={showLogsModal}
                    onClose={() => setShowLogsModal(false)}
                />

                {/* Add Payment Dialog */}
                {billingDraft && billingDraft.type !== 'INFLUENCER' && billingDraft.type !== 'MARKETING' && billingDraft.type !== 'KULANZ' && (
                    <AddPaymentDialog
                        storeId={billingDraft.storeId}
                        open={showAddPaymentDialog}
                        onOpenChange={setShowAddPaymentDialog}
                        billingDraftId={billingDraft.id}
                        remainingAmountEur={(() => {
                            const totalPaidCents = payments?.reduce((sum, p) =>
                                p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                            ) ?? 0;
                            const totalPaidEur = totalPaidCents / 100;
                            const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                            return Math.max(0, Math.round((billingDraftTotal - totalPaidEur) * 100) / 100);
                        })()}
                        onSuccess={() => {
                            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id });
                        }}
                    />
                )}

                {/* Edit Billing Draft Dialog */}
                {billingDraft && showEditDialog && (
                    <BillingDraftEditor
                        open={showEditDialog}
                        onOpenChange={setShowEditDialog}
                        billingDraft={billingDraft}
                        onSuccess={() => {
                            trpcUtils.billingDrafts.getOne.invalidate({ id });
                            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id });
                            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id });
                        }}
                    />
                )}

                {/* Refund Payment Dialog */}
                <RefundPaymentDialog
                    open={showRefundPaymentDialog}
                    onOpenChange={(open) => {
                        if (!open) setShowRefundPaymentDialog(false);
                    }}
                    billingDraftId={billingDraft.id}
                    storeId={billingDraft.storeId}
                    payments={payments}
                    totalGrossDiscounted={Number(billingDraft.totalGrossDiscounted)}
                    onSuccess={() => {
                        trpcUtils.billingDrafts.getOne.invalidate({ id });
                        trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id });
                    }}
                />

                {/* Cancel Line Item Dialog */}
                <AlertDialog
                    open={!!cancelLineItemTarget}
                    onOpenChange={(open) => {
                        if (!open) {
                            setCancelLineItemTarget(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Position stornieren</AlertDialogTitle>
                            <AlertDialogDescription>
                                Diese Aktion kann nicht rückgängig gemacht werden. Es wird eine Storno-Rechnung für die Position &quot;{cancelLineItemTarget?.description}&quot; erstellt.
                            </AlertDialogDescription>
                        </AlertDialogHeader>

                        {cancelLineItemTarget?.bundleVoucherTransactions && cancelLineItemTarget.bundleVoucherTransactions.length > 0 && (
                            <div className='space-y-3 border rounded-lg p-3 bg-muted/50'>
                                <p className='text-sm font-medium'>Bundle-Gutschein Nutzung</p>
                                <div className='space-y-2'>
                                    {cancelLineItemTarget.bundleVoucherTransactions.map((tx: any) => (
                                        <div key={tx.id} className='text-sm flex items-center justify-between'>
                                            <span className='text-muted-foreground'>
                                                {tx.bundleVoucher?.treatmentVariant?.name || 'Bundle-Gutschein'}
                                            </span>
                                            <span className='font-mono'>
                                                {Math.abs(tx.quantityUnits)} {Math.abs(tx.quantityUnits) === 1 ? 'Einheit' : 'Einheiten'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className='flex items-center space-x-2 pt-1'>
                                    <Checkbox
                                        id='reverse-bundle-voucher-detail'
                                        checked={reverseBundleVoucher}
                                        onCheckedChange={(checked) => setReverseBundleVoucher(checked === true)}
                                    />
                                    <Label htmlFor='reverse-bundle-voucher-detail' className='text-sm cursor-pointer'>
                                        Bundle-Gutschein Nutzung rückgängig machen
                                    </Label>
                                </div>
                                {reverseBundleVoucher && (
                                    <p className='text-xs text-muted-foreground'>
                                        Die verbrauchten Einheiten werden dem Bundle-Gutschein wieder gutgeschrieben.
                                    </p>
                                )}
                            </div>
                        )}

                        {isLastActiveLineItem && (() => {
                            const giftCardPayments = (payments || []).filter((p: any) =>
                                p.paymentMethod === 'GIFT_CARD' && p.type === 'BILLING_DRAFT_PAYMENT' && p.paidAmountCents > 0
                            );
                            if (giftCardPayments.length === 0) {
                                return null;
                            }
                            const totalGiftCardCents = giftCardPayments.reduce((sum: number, p: any) => sum + p.paidAmountCents, 0);
                            return (
                                <div className='space-y-3 border rounded-lg p-3 bg-muted/50'>
                                    <p className='text-sm font-medium'>Gutscheinkarte Zahlung</p>
                                    <p className='text-sm text-muted-foreground'>
                                        {formatCurrency(totalGiftCardCents / 100)} wurde mit Gutscheinkarte(n) bezahlt.
                                    </p>
                                    <div className='flex items-center space-x-2 pt-1'>
                                        <Checkbox
                                            id='reverse-gift-card-detail'
                                            checked={reverseGiftCard}
                                            onCheckedChange={(checked) => setReverseGiftCard(checked === true)}
                                        />
                                        <Label htmlFor='reverse-gift-card-detail' className='text-sm cursor-pointer'>
                                            Gutscheinkarte Guthaben zurückbuchen
                                        </Label>
                                    </div>
                                    {reverseGiftCard && (
                                        <p className='text-xs text-muted-foreground'>
                                            Der bezahlte Betrag wird der Gutscheinkarte gutgeschrieben.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}

                        <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => {
                                    if (cancelLineItemTarget) {
                                        cancelLineItemMutation.mutate({
                                            lineItemId: cancelLineItemTarget.id,
                                            reverseBundleVoucher,
                                            reverseGiftCard,
                                        });
                                        setCancelLineItemTarget(null);
                                    }
                                }}
                                className='bg-destructive text-white hover:bg-destructive/90'
                            >
                                Stornieren
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Bundle Cancel Warning Dialog */}
                <AlertDialog
                    open={!!bundleCancelWarningInfo}
                    onOpenChange={(open) => {
                        if (!open) {
                            setBundleCancelWarningInfo(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Stornierung nicht möglich</AlertDialogTitle>
                            <AlertDialogDescription>
                                Das Bundle wurde bereits genutzt und kann daher nicht storniert werden.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        {bundleCancelWarningInfo && (
                            <div className='space-y-2'>
                                {bundleCancelWarningInfo.vouchers.map((v, idx) => (
                                    <div key={idx} className='border rounded-lg p-3 bg-muted/50'>
                                        <p className='text-sm font-medium'>{v.treatmentVariantName}</p>
                                        <p className='text-sm text-muted-foreground'>
                                            {v.usedUnits} von {v.unitQuantity} Einheiten verbraucht
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                        <AlertDialogFooter>
                            <AlertDialogAction onClick={() => setBundleCancelWarningInfo(null)}>
                                Verstanden
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Bundle Cancel Confirmation Dialog */}
                <AlertDialog
                    open={!!bundleCancelConfirmInfo}
                    onOpenChange={(open) => {
                        if (!open) {
                            setBundleCancelConfirmInfo(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Bundle stornieren und löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Durch die Stornierung wird das zugehörige Bundle unwiderruflich gelöscht. Alle verbleibenden Einheiten werden entfernt.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        {bundleCancelConfirmInfo && (
                            <div className='space-y-2'>
                                {bundleCancelConfirmInfo.vouchers.map((v, idx) => (
                                    <div key={idx} className='border rounded-lg p-3 bg-destructive/10'>
                                        <p className='text-sm font-medium'>{v.treatmentVariantName}</p>
                                        <p className='text-sm text-muted-foreground'>
                                            {v.remainingUnits} von {v.unitQuantity} Einheiten werden entfernt
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                        {isLastActiveLineItem && (() => {
                            const giftCardPayments = (payments || []).filter((p: any) =>
                                p.paymentMethod === 'GIFT_CARD' && p.type === 'BILLING_DRAFT_PAYMENT' && p.paidAmountCents > 0
                            );
                            if (giftCardPayments.length === 0) {
                                return null;
                            }
                            const totalGiftCardCents = giftCardPayments.reduce((sum: number, p: any) => sum + p.paidAmountCents, 0);
                            return (
                                <div className='space-y-3 border rounded-lg p-3 bg-muted/50'>
                                    <p className='text-sm font-medium'>Gutscheinkarte Zahlung</p>
                                    <p className='text-sm text-muted-foreground'>
                                        {formatCurrency(totalGiftCardCents / 100)} wurde mit Gutscheinkarte(n) bezahlt.
                                    </p>
                                    <div className='flex items-center space-x-2 pt-1'>
                                        <Checkbox
                                            id='reverse-gift-card-bundle-detail'
                                            checked={reverseGiftCard}
                                            onCheckedChange={(checked) => setReverseGiftCard(checked === true)}
                                        />
                                        <Label htmlFor='reverse-gift-card-bundle-detail' className='text-sm cursor-pointer'>
                                            Gutscheinkarte Guthaben zurückbuchen
                                        </Label>
                                    </div>
                                    {reverseGiftCard && (
                                        <p className='text-xs text-muted-foreground'>
                                            Der bezahlte Betrag wird der Gutscheinkarte gutgeschrieben.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}
                        <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => {
                                    if (bundleCancelConfirmInfo) {
                                        cancelBundleWithArchiveMutation.mutate({
                                            lineItemId: bundleCancelConfirmInfo.lineItemId,
                                            reverseGiftCard,
                                        });
                                    }
                                }}
                                className='bg-destructive text-white hover:bg-destructive/90'
                            >
                                {cancelBundleWithArchiveMutation.isPending ? (
                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                ) : null}
                                Stornieren und löschen
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Gift Card Cancel Warning Dialog */}
                <AlertDialog
                    open={!!giftCardCancelWarningInfo}
                    onOpenChange={(open) => {
                        if (!open) {
                            setGiftCardCancelWarningInfo(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Stornierung nicht möglich</AlertDialogTitle>
                            <AlertDialogDescription>
                                Die Gutscheinkarte wurde bereits genutzt und kann daher nicht storniert werden.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        {giftCardCancelWarningInfo && (
                            <div className='space-y-2'>
                                {giftCardCancelWarningInfo.giftCards.map((gc, idx) => (
                                    <div key={idx} className='border rounded-lg p-3 bg-muted/50'>
                                        <p className='text-sm font-medium'>Gutscheinkarte {gc.giftCardNumber}</p>
                                        <p className='text-sm text-muted-foreground'>
                                            {formatCurrency(gc.usedAmount)} von {formatCurrency(gc.initialValueEur)} verbraucht
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                        <AlertDialogFooter>
                            <AlertDialogAction onClick={() => setGiftCardCancelWarningInfo(null)}>
                                Verstanden
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Gift Card Cancel Confirmation Dialog */}
                <AlertDialog
                    open={!!giftCardCancelConfirmInfo}
                    onOpenChange={(open) => {
                        if (!open) {
                            setGiftCardCancelConfirmInfo(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Gutscheinkarte stornieren und löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Durch die Stornierung wird die zugehörige Gutscheinkarte unwiderruflich gelöscht. Das gesamte Guthaben wird entfernt.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        {giftCardCancelConfirmInfo && (
                            <div className='space-y-2'>
                                {giftCardCancelConfirmInfo.giftCards.map((gc, idx) => (
                                    <div key={idx} className='border rounded-lg p-3 bg-destructive/10'>
                                        <p className='text-sm font-medium'>Gutscheinkarte {gc.giftCardNumber}</p>
                                        <p className='text-sm text-muted-foreground'>
                                            Guthaben {formatCurrency(gc.remainingBalance)} von {formatCurrency(gc.initialValueEur)} wird entfernt
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                        {isLastActiveLineItem && (() => {
                            const giftCardPayments = (payments || []).filter((p: any) =>
                                p.paymentMethod === 'GIFT_CARD' && p.type === 'BILLING_DRAFT_PAYMENT' && p.paidAmountCents > 0
                            );
                            if (giftCardPayments.length === 0) {
                                return null;
                            }
                            const totalGiftCardCents = giftCardPayments.reduce((sum: number, p: any) => sum + p.paidAmountCents, 0);
                            return (
                                <div className='space-y-3 border rounded-lg p-3 bg-muted/50'>
                                    <p className='text-sm font-medium'>Gutscheinkarte Zahlung</p>
                                    <p className='text-sm text-muted-foreground'>
                                        {formatCurrency(totalGiftCardCents / 100)} wurde mit Gutscheinkarte(n) bezahlt.
                                    </p>
                                    <div className='flex items-center space-x-2 pt-1'>
                                        <Checkbox
                                            id='reverse-gift-card-giftcard-detail'
                                            checked={reverseGiftCard}
                                            onCheckedChange={(checked) => setReverseGiftCard(checked === true)}
                                        />
                                        <Label htmlFor='reverse-gift-card-giftcard-detail' className='text-sm cursor-pointer'>
                                            Gutscheinkarte Guthaben zurückbuchen
                                        </Label>
                                    </div>
                                    {reverseGiftCard && (
                                        <p className='text-xs text-muted-foreground'>
                                            Der bezahlte Betrag wird der Gutscheinkarte gutgeschrieben.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}
                        <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => {
                                    if (giftCardCancelConfirmInfo) {
                                        cancelGiftCardWithArchiveMutation.mutate({
                                            lineItemId: giftCardCancelConfirmInfo.lineItemId,
                                            reverseGiftCard,
                                        });
                                    }
                                }}
                                className='bg-destructive text-white hover:bg-destructive/90'
                            >
                                {cancelGiftCardWithArchiveMutation.isPending ? (
                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                ) : null}
                                Stornieren und löschen
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-6xl max-h-[90vh] overflow-y-auto'>
                <DialogHeader>
                    <DialogTitle>Auftrag</DialogTitle>
                </DialogHeader>
                {renderContent()}
            </DialogContent>
        </Dialog>
    );
};

export default BillingDraftDetailDialog;
